<template>
  <el-container>
    <el-main>
      <div class="home">
        <el-card class="ta-main">

          <div class="ta-header">
            {{$t('general.title')}}
          </div>

          <el-row class="ta-row">
            <el-col :span="16" class="ta-label">
              {{$t('general.date')}}
            </el-col>
            <el-col :span="8">
              <el-date-picker class="ta-input"
                              v-model="date"
                              format="dd.MM.yyyy"/>
            </el-col>
          </el-row>

          <el-row class="ta-row">
            <el-col :span="16" class="ta-label">
              {{$t('general.label')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.turbineNumber" class="ta-input"/>
            </el-col>
          </el-row>

          <el-row class="ta-row">
            <el-col :span="16" class="ta-label">
              {{$t('general.power')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.power" type="number" class="ta-input" disabled/>
            </el-col>
          </el-row>

          <el-row class="ta-row" :class="{'ta-row__error': !isValid(info.frequency)}">
            <el-col :span="16" class="ta-label">
              {{$t('general.frequency')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.frequency" type="number" class="ta-input" disabled/>
            </el-col>
          </el-row>

          <el-row class="ta-row" :class="{'ta-row__error': !isValid(info.bladeCount)}">
            <el-col :span="16" class="ta-label">
              {{$t('general.bladeCount')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.bladeCount" type="number" class="ta-input" disabled/>
            </el-col>
          </el-row>

          <el-row class="ta-row" :class="{'ta-row__error': !isValid(info.directionBladeCount)}">
            <el-col :span="16" class="ta-label">
              {{$t('general.directionBladeCount')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.directionBladeCount" type="number" class="ta-input" disabled/>
            </el-col>
          </el-row>

          <el-row class="ta-row" :class="{'ta-row__error': !isValid(info.yearsOfUse)}">
            <el-col :span="16" class="ta-label">
              {{$t('general.yearsOfUse')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.yearsOfUse" type="number" class="ta-input"/>
            </el-col>
          </el-row>

          <el-row class="ta-row">
            <el-col :span="16" class="ta-label">
              {{$t('general.maxHeight')}}
            </el-col>
            <el-col :span="8">
              <el-popover
                placement="top-start"
                trigger="hover"
                :content="$t('general.maxHeightHint')">
                <el-input-number slot="reference" v-model="info.maxHeight" controls-position="right" :min="92"
                                 :max="106" class="ta-input"/>
              </el-popover>
            </el-col>
          </el-row>

          <el-row class="ta-row">
            <el-col :span="16" class="ta-label" :class="{'ta-row__error': !isValid(info.unrelievedStress)}">
              {{$t('general.unrelievedStress')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.unrelievedStress" type="number" class="ta-input"/>
            </el-col>
          </el-row>

        </el-card>

        <el-card class="ta-main">
          <div class="ta-header">
            {{$t('hours.title')}}
          </div>

          <el-row class="ta-row" :class="{'ta-row__error': !isValid(hours.total)}">
            <el-col :span="16" class="ta-label">
              {{$t('hours.total')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="hours.total" type="number" class="ta-input"/>
            </el-col>
          </el-row>

          <el-row class="ta-row ta-row__label">
            <el-col :span="24" class="ta-label ta-label__bold">
              {{$t('hours.rangesTitle')}}
            </el-col>
          </el-row>

          <div v-for="(zone, index) in calculatedZones" :key="zone.label">
            <div class="ta-row-block">
              <el-col :span="12" class="ta-label">
                {{$t('hours.' + zone.label)}}
              </el-col>
              <el-col :span="12" class="ta-label">
                <div v-for="range in zone.ranges" :key="zone.label + range.label ">
                  <el-row class="ta-row ta-row__inner" :class="{'ta-row__error': !isValid(range.value)}">
                    <el-col :span="8">
                      {{$t('hours.' + range.label)}}
                    </el-col>
                    <el-col :span="16" v-if="range.hint">
                      <el-input v-model="range.displayValue" type="number" class="ta-input" :disabled="range.disabled">
                        <template slot="append">
                          <el-popover
                            placement="top-start"
                            trigger="hover"
                            :content="range.hint">
                            <el-button slot="reference">?</el-button>
                          </el-popover>
                        </template>
                      </el-input>
                    </el-col>
                    <el-col :span="16" v-else>
                      <el-input v-model="range.value" type="number" class="ta-input" :disabled="range.disabled"/>
                    </el-col>
                  </el-row>
                </div>
              </el-col>
            </div>
            <el-divider v-if="index !== hours.zones.length - 1"/>
          </div>

          <el-row class="ta-row" :class="{'ta-row__error': !isValid(info.startStopCount)}">
            <el-col :span="16" class="ta-label">
              {{$t('hours.startStopCount')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.startStopCount" type="number" class="ta-input"/>
            </el-col>
          </el-row>
        </el-card>

        <el-card class="ta-main">

          <div class="ta-header">
            {{$t('futureHours.title')}}
          </div>

          <el-row class="ta-row" :class="{'ta-row__error': !isValid(hours.total)}">
            <el-col :span="16" class="ta-label">
              {{$t('futureHours.yearsUntilRepair')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.yearsUntilRepair" type="number" class="ta-input"/>
            </el-col>
          </el-row>

          <el-row class="ta-row ta-row__label">
            <el-col :span="24" class="ta-label ta-label__bold">
              {{$t('hours.rangesTitle')}}
            </el-col>
          </el-row>

          <div v-for="(zone, index) in calculatedZones" :key="zone.label + '_2'">
            <div class="ta-row-block">
              <el-col :span="12" class="ta-label">
                {{$t('hours.' + zone.label)}}
              </el-col>
              <el-col :span="12" class="ta-label">
                <div v-for="range in zone.ranges" :key="zone.label + range.label + '_2'">
                  <el-row class="ta-row ta-row__inner" :class="{'ta-row__error': !isValid(range.futureValue)}">
                    <el-col :span="8">
                      {{$t('hours.' + range.label)}}
                    </el-col>
                    <el-col :span="16">
                      <el-input v-model="range.futureValue" type="number" class="ta-input"/>
                    </el-col>
                  </el-row>
                </div>
              </el-col>
            </div>
            <el-divider v-if="index !== hours.zones.length - 1"/>
          </div>

          <el-row class="ta-row" :class="{'ta-row__error': !isValid(info.futureStartStopCount)}">
            <el-col :span="16" class="ta-label">
              {{$t('hours.startStopCount')}}
            </el-col>
            <el-col :span="8">
              <el-input v-model="info.futureStartStopCount" type="number" class="ta-input"/>
            </el-col>
          </el-row>

        </el-card>

      </div>
    </el-main>
    <el-footer class="ta-footer">
      <el-button @click="calculate" :disabled="loading">
        {{$t('runBtn')}}
      </el-button>
    </el-footer>
  </el-container>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import moment from 'moment'
import store from '../data/store'

export default {
  name: 'data-input',
  data () {
    return {
      date: moment(),
      loading: false,
      invalid: false,
      info: {
        turbineNumber: 'ГА-3 (DEMO)',
        power: 220,
        frequency: 2.08,
        bladeCount: 14,
        directionBladeCount: 24,
        yearsOfUse: 50,
        startStopCount: 4000,
        futureStartStopCount: 1500,
        maxHeight: 105,
        unrelievedStress: 100,
        yearsUntilRepair: 7,
        requestData: {
          ip: null,
          country: null
        }
      },
      hours: {
        total: 240000,
        zones: [
          {
            label: 'zone0',
            ranges: [
              {
                label: 'range0',
                value: 22000,
                futureValue: 3500
              }
            ]
          },
          {
            label: 'zone1',
            ranges: [
              {
                label: 'range1',
                value: 39000,
                futureValue: 5000
              }, {
                label: 'range2',
                value: 8000,
                futureValue: 1300
              }
            ]
          },
          {
            label: 'zone2',
            ranges: [
              {
                label: 'range3',
                futureValue: 50,
                value: null,
                displayValue: null,
                disabled: true,
                function: this.calculate80to120Value,
                hint: 'Расчет производится автоматически по числу пусков'
              }
            ]
          },
          {
            label: 'zone3',
            ranges: [
              {
                label: 'range4',
                value: 700,
                futureValue: 120
              }, {
                label: 'range5',
                value: 1000,
                futureValue: 160
              }, {
                label: 'range6',
                value: 4000,
                futureValue: 500
              }, {
                label: 'range7',
                value: 10000,
                futureValue: 1600
              }, {
                label: 'range8',
                value: 136000,
                futureValue: 22000
              }, {
                label: 'range9',
                futureValue: 3000,
                value: null,
                displayValue: null,
                disabled: true,
                function: this.calculate220to240Value,
                hint: 'Расчет производится автоматически по общей наработке'
              }
            ]
          }
        ]
      }
    }
  },
  computed: {
    calculatedZones () {
      for (let zone of this.hours.zones) {
        for (let range of zone.ranges) {
          if (range.value) {
            range.value = parseInt(range.value)
          }
          if (range.function) {
            range.value = 0
            range.value = range.function()
            range.displayValue = Math.round(range.value)
          }
        }
      }

      return this.hours.zones
    }
  },
  watch: {
    'info.startStopCount' () {
      const value = this.calculate80to120Value()
      this.hours.zones[2].ranges[0].value = value
      this.hours.zones[2].ranges[0].displayValue = Math.round(value)
    }
  },
  filters: {
    toInt: function (value) {
      if (!value) return ''
      return Math.round(value)
    }
  },
  methods: {
    isValid (value) {
      return this.invalid ? !!value : true
    },
    calculate80to120Value () {
      // TODO: fix formula using individual data
      return 2 * this.info.startStopCount / 60
    },
    calculate220to240Value () {
      let calculatedTotal = 0
      for (let zone of this.hours.zones) {
        for (let range of zone.ranges) {
          calculatedTotal += range.value
        }
      }
      const diff = this.hours.total - calculatedTotal
      return diff > 0 ? diff : null
    },
    validate (data) {
      this.invalid = false
      if (!data.frequency ||
        !data.bladeCount ||
        !data.directionBladeCount ||
        !data.yearsOfUse ||
        !data.startStopCount ||
        !data.futureStartStopCount ||
        !data.maxHeight ||
        !data.unrelievedStress) {
        this.invalid = true
        return
      }

      for (const zone of data.hours.zones) {
        for (const range of zone.ranges) {
          if (!range.value || !range.futureValue) {
            this.invalid = true
            return
          }
        }
      }
    },
    calculate () {
      const data = _.cloneDeep(this.info)
      data.hours = {
        total: this.hours.total,
        zones: _.cloneDeep(this.calculatedZones)
      }
      this.validate(data)

      if (this.invalid) {
        this.$message({
          message: 'Пожалуйста, заполните все необходимые поля.',
          type: 'error'
        })
        return
      }

      const loading = this.$loading({
        lock: true,
        text: this.$t('waitingText'),
        customClass: 'ta-loading'
      })
      axios.post('api/v1/analyze', data)
        .then(r => r.data)
        .then(d => {
          loading.close()
          store.commit('storeResult', d)
          const locale = this.$route.query.lang || 'ru'
          this.$router.push({ path: 'Result', query: { lang: locale } })
        })
    }
  },
  mounted () {
    axios.get('http://ip-api.com/json')
      .then(r => r.data)
      .then(d => {
        this.info.requestData.ip = d.query
        this.info.requestData.country = d.country
      })
  }
}
</script>

<style lang="scss">
  .home {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
    flex-wrap: wrap;
    align-content: stretch;

    .ta-main {
      background: #F1F6E4;
      margin: 10pt;
      max-width: 800px;
      flex-basis: 600px;
      flex-grow: 1;

      @media all and (max-width: 500px) {
        margin-left: 0;
        margin-right: 0;
      }

      .ta-row {
        padding: 5px 20px;
        display: flex;
        flex-direction: row;
        align-items: center;
        vertical-align: middle;

        &:nth-of-type(even) {
          background: #E0F2D9;
        }

        &__error {
          background: #dc737d !important;
        }

        &__label {
          margin-top: 10pt;
          background: #F1F6E4 !important;
        }

        &:nth-of-type(odd) {
          background: #F1F6E4;
        }

        &__inner {
          padding-left: 0;
          padding-right: 0;
        }

        .ta-input {
          width: 100%;
        }
      }

      .ta-row-block {
        padding: 5px 20px;
        background: none;
        display: flex;
        flex-direction: row;
        align-items: center;
        vertical-align: middle;
      }
    }
  }

  .ta-label {
    text-align: start;

    &__bold {
      font-weight: bold;
    }
  }

  .ta-header {
    padding-bottom: 20px;
    font-weight: bold;
    font-size: large;
  }

  .ta-footer {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ta-loading {
    .el-loading-text {
      font-size: large !important;
      color: #2c3e50 !important;
    }
  }
</style>
